import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import 'form-pattern/dist/main.css'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useLocation } from 'react-router-dom'
import { createSelector } from '@reduxjs/toolkit'
import BreadCrumbs from '../../../components/common/BreadCrumbs/BreadCrumbs'
import Datepicker from '../../../components/common/DatePicker/Datepicker'
import ValidationBanner from '../../../components/common/ValidationBanner'
import Table from '../../../components/common/Table/Table'
import Dropdown, { DropdownOption } from '../../../components/common/Dropdown'
import { TextInputField } from '../../../components/common/FormFields'
import { ValidationErrorDisplay } from '../../../components/common/FormFields/ValidationErrorDisplay'
import Loader from '../../../components/common/Loader'
import PencilIcon from '../../../../public/assets/images/pencil-outline.svg'
import MinusCircleIcon from '../../../../public/assets/images/minus-circle-outline.svg'
import { TimeOffRequestDetails, isVacationBid } from '../../../store/slices/Requestor/timeOffDetailsSlice'
import { getOtherReqDetails } from '../../../store/slices/Requestor/createNewRequestSlice'
import { validateVacationBiddingEntries, validateDateGaps } from '../../../components/common/Validation/validationEngine'
import ValidationMessages from '../../../components/common/Validation/validationMessageConfig'
import { getTimeOffTypeLabel } from '../../../utils/timeOffTypes'
import { RootState, AppDispatch } from '../../../store/store'
import { scrollToTopAndFocusAlert } from '../../../utils/scrollUtils'
import { useCreateTimeOffRequest } from '../../../hooks/useCreateTimeOffRequest'
import { convertMMDDYYYYToYYYYMMDD, formatManualRequestReceivedDate, isPastDate } from '../../../utils/date'
import { formatHours } from '../../../utils/formatters'
import ManualRequestedDate from '../../../components/common/ManualRequestedDate'
import { LabeledValue } from '../../../components/common/LabeledValue'
import ManualTimeOffBanner from '../../../components/common/ManualTimeOffBanner'
import { isVacationBidOverlapForDate, sortRowsByDate } from '../StatusViews/detail/constants'
import ColorLegendTooltip from '../../../components/common/ColorLegendTooltip'


interface VacationBidEntry {
  id: string
  dates: string[]
  hoursPerDay: string | Record<string, string>
  totalHours: number
  comments: string
  choiceRank?: string
}

const getDateHours = (bid: VacationBidEntry, dateStr: string): string => {
  if (typeof bid.hoursPerDay === 'string') return bid.hoursPerDay
  return bid.hoursPerDay[dateStr] || '8'
}


interface TableDisplayProps {
  vacationBids: VacationBidEntry[]
  onEdit: (id: string, dateStr: string) => void
  onRemove: (bidId: string, dateStr: string) => void
  otherReqData: Record<string, any[]> | null | undefined
  /** The request currently being edited/resubmitted — excluded from its own overlap check. */
  currentRequestId?: string
}

/**
 * Renders the "Day in Other Requests" column as a disabled checkbox
 * (matching legacy's read-only checked/unchecked checkbox), and falls back
 * to the plain cell value for every other column.
 */
const renderOverlapCheckboxCell = (row: any, col: { key: string }): React.ReactNode => {
  if (col.key !== 'hoursOtherRequests') return row[col.key]
  if (row.id === 'totals-row') return ''
  return (
    <input
      type="checkbox"
      className="table-cell-checkbox"
      checked={!!row.hoursOtherRequests}
      disabled
      readOnly
      aria-label="Day in other requests"
    />
  )
}

interface EditButtonProps {
  bidId: string
  dateStr: string
  onEdit: (id: string, dateStr: string) => void
}

const EditButton: React.FC<EditButtonProps> = ({ bidId, dateStr, onEdit }) => (
  <button
    className="flex items-center gap-2 bg-transparent border-0 p-0 cursor-pointer hover:opacity-80"
    type="button"
    aria-label="Edit"
    onClick={() => onEdit(bidId, dateStr)}
  >
    <img src={PencilIcon} alt="Edit" className="w-4 h-4" />
  </button>
)

interface RemoveButtonProps {
  bidId: string
  dateStr: string
  onRemove: (bidId: string, dateStr: string) => void
}

const RemoveButton: React.FC<RemoveButtonProps> = ({ bidId, dateStr, onRemove }) => (
  <button
    className="flex items-center gap-2 bg-transparent border-0 p-0 cursor-pointer hover:opacity-80"
    type="button"
    aria-label="Remove"
    onClick={() => onRemove(bidId, dateStr)}
  >
    <img src={MinusCircleIcon} alt="Remove" className="w-4 h-4" />
  </button>
)

const VacationBidTableDisplay = ({
  vacationBids,
  onEdit,
  onRemove,
  otherReqData,
  currentRequestId,
}: TableDisplayProps) => {
  const getTableRows = () => {
    const dataRows: any[] = []

    vacationBids.forEach((bid) => {
      bid.dates.forEach((dateStr) => {
        const dateObj = new Date(dateStr)
        const dayName = dateObj.toLocaleDateString('en-US', { weekday: 'long' })
        const formattedDate = dateObj.toLocaleDateString('en-US', {
          month: '2-digit',
          day: '2-digit',
          year: 'numeric',
        })

        const dateHours = getDateHours(bid, dateStr)

        dataRows.push({
          id: `${bid.id}-${dateStr}`,
          bidId: bid.id,
          requestType: 'Vacation Bidding',
          date: formattedDate,
          day: dayName,
          timesRequested: 'Full Day',
          hours: dateHours,
          hoursOtherRequests: isVacationBidOverlapForDate(otherReqData, dateStr, currentRequestId),
          edit: <EditButton bidId={bid.id} dateStr={dateStr} onEdit={onEdit} />,
          remove: <RemoveButton bidId={bid.id} dateStr={dateStr} onRemove={onRemove} />,
        })
      })
    })

    // Sort so the review table always reads in ascending date order, even
    // after "Add date to request" appends a newly picked date to the end of
    // a bid's `dates` array.
    const sortedDataRows = sortRowsByDate(dataRows)

    const totalHours = vacationBids.reduce((sum, bid) => sum + bid.totalHours, 0)
    const totalsRow = {
      id: 'totals-row',
      requestType: 'Total Hours',
      date: '',
      day: '',
      timesRequested: '',
      hours: formatHours(totalHours),
      hoursOtherRequests: false,
      edit: '',
      remove: '',
    }

    return [...sortedDataRows, totalsRow]
  }

  const columns = [
    { key: 'requestType', label: 'Request Type' },
    { key: 'date', label: 'Date' },
    { key: 'day', label: 'Day' },
    { key: 'timesRequested', label: 'Times' },
    { key: 'hours', label: 'Requested Hours' },
    { key: 'hoursOtherRequests', label: 'Day in Other Requests' },
    { key: 'edit', label: 'Edit' },
    { key: 'remove', label: 'Remove' },
  ]

  const rows = getTableRows()
  return (
    <Table
      columns={columns}
      rows={rows}
      renderCell={renderOverlapCheckboxCell}
      showPagination={false}
    />
  )
}

/**
 * Memoized selector to return otherReqDetails data with stable reference.
 * Prevents infinite render loops by only returning a new reference when the data actually changes.
 */
const EMPTY_OBJECT = {}
const selectOtherReqDetailsData = createSelector(
  (state: RootState) => state.otherReqDetails?.data,
  (data) => data ?? EMPTY_OBJECT
)

/**
 * Dropdown options for vacation bid choice rank.
 * Defined outside component to maintain stable reference across renders.
 */
const DROPDOWN_OPTIONS: DropdownOption[] = [
  { id: 1, label: '1', value: '1' },
  { id: 2, label: '2', value: '2' },
  { id: 3, label: '3', value: '3' },
  { id: 4, label: '4', value: '4' },
  { id: 5, label: '5', value: '5' },
  { id: 6, label: 'Not applicable', value: 'Not applicable' },
]

interface EditAndResubmitVacationBidProps {
  data: TimeOffRequestDetails
  onCancel?: () => void
  onResubmitSuccess?: () => void
}

const normalizeDate = (value: string): string => {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const year = date.getFullYear()
  return `${month}/${day}/${year}`
}

/**
 * Some validation messages need an explanatory sub-line under the banner
 * heading — matches the same heading+message treatment already used for
 * this message in the create flow, which this screen's banner otherwise
 * renders as a bare heading only.
 */
const getValidationBannerSubMessage = (msg: string): React.ReactNode | undefined =>
  msg === ValidationMessages.REQUEST_DATES_EXCEED_8_DAY_GAP ? (
    <p className="p3 -book m-0!">
      If these dates represent more than one time off request, please submit
      them as separate requests.
    </p>
  ) : undefined

/**
 * The date-gap message renders at the larger (non-compact) banner size in
 * the Vacation Bid create flow (CreateVacationBidRequest) — everything else
 * stays compact, and this Vacation Bid edit/resubmit flow should match it.
 */
const isExpandedValidationBanner = (msg: string): boolean =>
  msg === ValidationMessages.REQUEST_DATES_EXCEED_8_DAY_GAP

const buildBidHoursPerDate = (bid: VacationBidEntry): Record<string, string> => {
  const hoursPerDate: Record<string, string> = {}
  bid.dates.forEach((date) => {
    const normalizedDate = normalizeDate(date)
    hoursPerDate[normalizedDate] = getDateHours(bid, normalizedDate)
  })
  return hoursPerDate
}

const buildTooltipAllocations = (detailsArray: any[]): Array<{ typeLabel: string; hours: number }> => {
  const allocations: Array<{ typeLabel: string; hours: number }> = []
  detailsArray.forEach((detail) => {
    detail.timeOffAllocations.forEach((allocation: any) => {
      allocations.push({
        typeLabel: getTimeOffTypeLabel(allocation.toTypeCd),
        hours: Number(allocation.hours) || 0,
      })
    })
  })
  return allocations
}

// First allocation only — used as the compact hover-tooltip summary (mirrors
// CreateVacationBidRequest). The full per-request breakdown lives in
// individualRequests below, surfaced via the tooltip's "See more".
const buildFirstAllocation = (detail: any): Array<{ typeLabel: string; hours: number }> => {
  if (!detail.timeOffAllocations || detail.timeOffAllocations.length === 0) return []
  const firstAlloc = detail.timeOffAllocations[0]
  return [
    {
      typeLabel: getTimeOffTypeLabel(firstAlloc.toTypeCd),
      hours: Number(firstAlloc.hours) || 0,
    },
  ]
}

// One tooltip "individual request" entry, with its own full allocation breakdown.
const buildIndividualRequest = (detail: any) => {
  const allocations = buildTooltipAllocations([detail])
  return {
    startTime: detail.startTime,
    endTime: detail.endTime,
    allocations,
    totalHours: allocations.reduce((sum, a) => sum + a.hours, 0),
  }
}

// A date can carry multiple separate partial-day requests (from different
// employees, or a single request split across allocation types). Without this,
// the tooltip silently merges every request's allocations into one flat list —
// mirrors CreateVacationBidRequest's buildIndividualRequests.
const buildIndividualRequests = (
  durationLabel: string,
  detailsArray: any[],
  firstDetail: any
) => {
  if (durationLabel === 'Partial Day' && detailsArray.length > 1) {
    return detailsArray.map(buildIndividualRequest)
  }
  if (durationLabel === 'Partial Day' && firstDetail.timeOffAllocations?.length > 1) {
    return [buildIndividualRequest(firstDetail)]
  }
  return undefined
}

const isVacationBidOverlapDetail = (detail: any): boolean => {
  const isVacationBidRequest =
    detail.timeOffAllocations?.some((allocation: any) => allocation.toTypeCd === 'A') ?? false

  const isOverlapStatus =
    detail.status === 'SUBMITTED_ONHOLD' ||
    detail.status === 'PRTSUBMITTED_ONHOLD' ||
    detail.status === 'RETURNED' ||
    detail.status === 'PRTRETURNED'

  return isVacationBidRequest && isOverlapStatus
}

const EditAndResubmitVacationBid: React.FC<EditAndResubmitVacationBidProps> = ({
  data,
  onCancel,
  onResubmitSuccess,
}) => {
  const dispatch = useDispatch<AppDispatch>()
  const navigate = useNavigate()
  const location = useLocation()
  const { submitResubmittedVacationBid } = useCreateTimeOffRequest()
  const otherReqData = useSelector(selectOtherReqDetailsData)
  const isAdminMode=location.pathname.includes('/admin')
  // State management
  const [vacationBids, setVacationBids] = useState<VacationBidEntry[]>([])
  const [selectedDates, setSelectedDates] = useState<string[]>([])
  const [hoursPerDay, setHoursPerDay] = useState<string>('0')
  const [hoursPerDate, setHoursPerDate] = useState<Record<string, string>>({})
  const [showHistoryTable, setShowHistoryTable] = useState<boolean>(false)
  const [editMode, setEditMode] = useState<boolean>(false)
  const [editDate, setEditDate] = useState<string | null>(null)
  const [editingBidId, setEditingBidId] = useState<string | null>(null)
  const [editingOriginalDate, setEditingOriginalDate] = useState<string | null>(null)
  const [selectedRank, setSelectedRank] = useState<DropdownOption | null>(null)
  const [comments, setComments] = useState<string>('')
  const [bannerMessages, setBannerMessages] = useState<string[] | null>(null)
  const [hoursError, setHoursError] = useState<string>('')
  const [choiceRankError, setChoiceRankError] = useState<string>('')
  const [commentsError, setCommentsError] = useState<string>('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [hasEditsBeenMade, setHasEditsBeenMade] = useState<boolean>(false)
  const [adminReceivedDate, setAdminReceivedDate] = useState<string>('')
  const [isAddingMoreDates, setIsAddingMoreDates] = React.useState(false)
  const [adminReceivedDateError, setAdminReceivedDateError] = useState<string>('')


  // Track initial state for edit detection (resubmit flow)
  const initialStateRef = useRef<{
    vacationBids: VacationBidEntry[]
    comments: string
  } | null>(null)

  // Track the bid being edited so we can restore it if user cancels
  const editingBidRef = useRef<VacationBidEntry | null>(null)

  // Track if we've already initialized vacationBids from data prop (only once)
  const hasInitializedRef = useRef(false)



  /**
   * Initialize calendar to show the month/year of the first request date.
   * This ensures the calendar displays the correct month when the request dates are loaded.
   * Falls back to today's date if no request dates are available.
   */
  const [viewedMonth, setViewedMonth] = useState(() => {
    if (data?.requestedDates && data.requestedDates.length > 0) {
      const [, month] = data.requestedDates[0].date.split('-')
      return Number.parseInt(month, 10) - 1 // Convert to 0-indexed month
    }
    return new Date().getMonth()
  })

  const [viewedYear, setViewedYear] = useState(() => {
    if (data?.requestedDates && data.requestedDates.length > 0) {
      const [year] = data.requestedDates[0].date.split('-')
      return Number.parseInt(year, 10)
    }
    return new Date().getFullYear()
  })

  // Fetch other request details for calendar
  useEffect(() => {
    dispatch(getOtherReqDetails({ month: viewedMonth, year: viewedYear }))
  }, [viewedMonth, viewedYear, dispatch])

  // Track if any edits have been made (vacation bid resubmit flow only)
  useEffect(() => {
    if (!initialStateRef.current) return

    const bidsChanged = JSON.stringify(vacationBids) !== JSON.stringify(initialStateRef.current.vacationBids)
    const commentsChanged = comments !== initialStateRef.current.comments

    setHasEditsBeenMade(bidsChanged || commentsChanged)
  }, [vacationBids, comments])

  /**
   * When user modifies hoursPerDay:
   * - Update ONLY the currently focused date (editDate)
   * - Keep all other dates unchanged
   */
const handleHoursChange = useCallback((value: string) => {
  setHoursPerDay(value)

  if (!editDate) return

  setHoursPerDate((prev) => {
    const updated = { ...prev }

    /**
     * Original dates are the dates already present in the bid before editing.
     * Newly added dates are dates selected during this edit session.
     */
    const originalDates = new Set(editingBidRef.current?.dates ?? [])

    selectedDates.forEach((date) => {
      const isCurrentEditDate = date === editDate
      const isNewlyAddedDate = !originalDates.has(date)

      /**
       * Update:
       * 1. The currently focused date
       * 2. All newly added dates
       *
       * Do not overwrite original existing dates unless that original date
       * is the current focused edit date.
       */
      if (isCurrentEditDate || isNewlyAddedDate) {
        updated[date] = value
      }
    })

    return updated
  })
}, [editDate, selectedDates])

  /**
   * When data changes, update the calendar to show the request month
   * and pre-populate vacation bids from existing request data.
   * This ensures the calendar displays and highlights the correct request dates.
   * 
   * IMPORTANT: Only initialize ONCE on first mount. Never reset bids after that.
   * Use hasInitializedRef to ensure this only runs one time ever.
   */
  useEffect(() => {
    if (!data?.requestedDates || data.requestedDates.length === 0 || !isVacationBid(data) || hasInitializedRef.current) {
      return
    }

    hasInitializedRef.current = true

    const [year, month] = data.requestedDates[0].date.split('-')
    const requestMonth = Number.parseInt(month, 10) - 1
    const requestYear = Number.parseInt(year, 10)

    setViewedMonth(requestMonth)
    setViewedYear(requestYear)

    const initialBid: VacationBidEntry = {
      id: data.requestId || Date.now().toString(),
      dates: data.requestedDates.map((requestedDate) => {
        const [requestedYear, requestedMonth, requestedDay] = requestedDate.date.split('-')
        return `${requestedMonth}/${requestedDay}/${requestedYear}`
      }),
      hoursPerDay: data.requestedDates[0]?.requestedHours?.toString() || '8',
      totalHours: data.requestedDates.reduce((sum, requestedDate) => sum + (Number(requestedDate.requestedHours) || 0), 0),
      comments: data.employeeComments || '',
      choiceRank: data.requestRank?.toString() || '1',
    }

    setVacationBids([initialBid])
    setComments(data.employeeComments || '')
    const rankValue = data.requestRank?.toString() || '1'
    const rankOption = DROPDOWN_OPTIONS.find((option) => option.value === rankValue)
    if (rankOption) setSelectedRank(rankOption)
    setShowHistoryTable(true)

    initialStateRef.current ??= {
      vacationBids: [initialBid],
      comments: data.employeeComments || '',
    }
  }, [data])

  /**
   * Memoize allBidDates to maintain a stable array reference.
   * Include saved vacation bid dates + all currently-selected dates during edit mode.
   * This ensures the calendar highlights all dates being edited, including newly added ones.
   */
  const allBidDates = useMemo(() => {
    return vacationBids
      .filter((b): b is VacationBidEntry => b !== null && b !== undefined)
      .flatMap((b) => b.dates)
  }, [vacationBids])

  const committedDates = useMemo(
    () =>
      Array.from(new Set(allBidDates.map(normalizeDate))).sort(
        (a, b) => new Date(a).getTime() - new Date(b).getTime()
      ),
    [allBidDates]
  )

  const sortedCommittedDates = useMemo(
    () => [...committedDates].sort((a, b) => new Date(a).getTime() - new Date(b).getTime()),
    [committedDates]
  )

  const sortedCurrentlyAddingDates = useMemo(
    () =>
      selectedDates
        .filter((date) => !committedDates.includes(normalizeDate(date)))
        .sort((a, b) => new Date(a).getTime() - new Date(b).getTime()),
    [committedDates, selectedDates]
  )

  const displayedEditableDates = useMemo(() => {
    if (!editDate) return selectedDates

    const normalizedEditDate = normalizeDate(editDate)
    if (committedDates.includes(normalizedEditDate)) {
      return [normalizedEditDate]
    }

    if (sortedCurrentlyAddingDates.length > 0) {
      return sortedCurrentlyAddingDates
    }

    return [normalizedEditDate]
  }, [committedDates, editDate, selectedDates, sortedCurrentlyAddingDates])

  const displayedTotalHours = useMemo(
    () =>
      formatHours(
        displayedEditableDates
          .reduce((sum, date) => sum + (Number.parseFloat(hoursPerDate[date] || '0') || 0), 0)
      ),
    [displayedEditableDates, hoursPerDate]
  )

  // Convert API dates to calendar format
  // Exclude vacation bid dates to avoid color conflicts (they should show light/dark blue, not green)
  const otherRequestDates = useMemo(() => {
    const bidDatesSet = new Set(allBidDates)
    return Object.keys(otherReqData)
      .filter((dateStr) => {
        const [year, month, day] = dateStr.split('-')
        const formattedDate = `${month}/${day}/${year}`
        return !bidDatesSet.has(formattedDate)
      })
      .map((dateStr) => {
        const [year, month, day] = dateStr.split('-')
        return `${month}/${day}/${year}`
      })
  }, [otherReqData, allBidDates])

  const otherRequestDetailsForTooltip = useMemo(() => {
    const bidDatesSet = new Set(allBidDates)
    const result: Record<string, any> = {}
    Object.entries(otherReqData).forEach(([dateKey, detailsArray]) => {
      // Skip vacation bid dates - they should show vacation bid info, not other request info
      const [year, month, day] = dateKey.split('-')
      const formattedDate = `${month}/${day}/${year}`
      if (bidDatesSet.has(formattedDate)) return
      
      if (!Array.isArray(detailsArray) || detailsArray.length === 0) return

      const firstDetail = detailsArray[0]
      const durationLabel =
        firstDetail.durationFlag === 'F' ? 'Full Day' : 'Partial Day'

      // Compact summary (first allocation only) for the hover preview — when a
      // date carries more than one separate request, the full per-request
      // breakdown goes in individualRequests instead, so the hover doesn't
      // merge unrelated requests' allocations into one flat, misleading list.
      const tooltipAllocations = buildFirstAllocation(firstDetail)
      const tooltipTotalHours = tooltipAllocations.reduce((sum, a) => sum + a.hours, 0)

      const vacationBidOverlapDetail = detailsArray.find(isVacationBidOverlapDetail)

      const tooltipEntry: any = {
        durationLabel,
        date: formattedDate,
        allocations: tooltipAllocations,
        totalHours: tooltipTotalHours,
        hasMultipleRequests:
          detailsArray.length > 1 || firstDetail.timeOffAllocations?.length > 1,
        status: vacationBidOverlapDetail?.status ?? firstDetail.status,
        otherRequests: detailsArray.flatMap(detail => detail.otherRequests ?? []),
      }

      if (firstDetail.startTime) tooltipEntry.startTime = firstDetail.startTime
      if (firstDetail.endTime) tooltipEntry.endTime = firstDetail.endTime

      tooltipEntry.individualRequests = buildIndividualRequests(
        durationLabel,
        detailsArray,
        firstDetail
      )

      result[dateKey] = tooltipEntry
    })
    return result
  }, [otherReqData, allBidDates])

  /**
   * Handler for resubmitting vacation bid with potentially updated dates
   * Always submits as RESUBMITTED status via submitResubmittedVacationBid
   */
  const handleResubmit = async () => {
    if (vacationBids.length === 0) {
      setBannerMessages(['Please add at least one vacation bid date'])
      scrollToTopAndFocusAlert()
      return
    }

    if (comments.trim() === '') {
      setCommentsError('Required')
      setBannerMessages(['Resubmitted comments are required'])
      scrollToTopAndFocusAlert()
      return
    }

    // Validate admin received date when in admin mode
    if (isAdminMode) {
      if (!adminReceivedDate.trim()) {
        setAdminReceivedDateError('Required')
        setBannerMessages(['Date Manual Request Received is required'])
        scrollToTopAndFocusAlert()
        return
      }
      if (adminReceivedDateError) {
        setBannerMessages([adminReceivedDateError])
        scrollToTopAndFocusAlert()
        return
      }
    }
    setAdminReceivedDateError('')
    setCommentsError('')
    setIsSubmitting(true)
    try {
      const results = await Promise.all(
        vacationBids.map(async (bid) => {
          const apiDates = bid.dates.map(convertMMDDYYYYToYYYYMMDD)
          
          const hours = typeof bid.hoursPerDay === 'string' ? bid.hoursPerDay : '8'
          const hoursValues = { vacation: hours, float: '0', flexible: '0', other: '0' }
          let perDateHoursValues: Record<string, typeof hoursValues> | undefined
          
          if (typeof bid.hoursPerDay === 'object') {
            perDateHoursValues = {}
            bid.dates.forEach((dateStr) => {
              const apiDateStr = convertMMDDYYYYToYYYYMMDD(dateStr)
              const dateHours = (bid.hoursPerDay as Record<string, string>)[dateStr] || '8'
              perDateHoursValues![apiDateStr] = { vacation: dateHours, float: '0', flexible: '0', other: '0' }
            })
          }

          return submitResubmittedVacationBid({
            selectedDates: apiDates,
            hoursValues,
            duration: 'Full Day',
            comments: comments.trim(),
            torRank: Number.parseInt(bid.choiceRank || '1', 10),
            perDateHoursValues,
            toReqId: data.requestId,
            manualReqRcvdDate: isAdminMode ? formatManualRequestReceivedDate(adminReceivedDate) : undefined,
          })
        })
      )

      const allSucceeded = results.every((r) => r.success)
      if (!allSucceeded) {
        const errors = results.filter((r) => !r.success).map((r) => r.error)
        setBannerMessages(errors.map(String))
        scrollToTopAndFocusAlert()
        setIsSubmitting(false)
        return
      }

      // Navigate to resubmitted vacation bid detail view via status summary page
      const firstResubmittedId = results[0]?.timeOffRequestId || results[0]?.toReqId || ''
      if (firstResubmittedId) {
        const route = isAdminMode
          ? `/admin/requestor/status/${firstResubmittedId}`
          : `/requestor/status/${firstResubmittedId}`

        navigate(route, {
          state: { showSubmittedBanner: true },
        })

        return
      }

      onResubmitSuccess?.()
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to resubmit vacation bid'
      setBannerMessages([errorMessage])
      scrollToTopAndFocusAlert()
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleMonthYearChange = useCallback((month: number, year: number) => {
    setViewedMonth(month)
    setViewedYear(year)
  }, [])

  const openCommittedVacationBidDate = useCallback(
    (bid: VacationBidEntry, clickedDate: string) => {
      const normalizedClickedDate = normalizeDate(clickedDate)
      const currentNewDates = selectedDates.filter(
        (date) => !committedDates.includes(normalizeDate(date))
      )
      const selectedOption =
        DROPDOWN_OPTIONS.find((option) => option.value === bid.choiceRank) || null

      editingBidRef.current = bid
      setSelectedDates(Array.from(new Set([normalizedClickedDate, ...currentNewDates])))
      setEditingBidId(bid.id)
      setHoursPerDate(buildBidHoursPerDate(bid))
      setSelectedRank(selectedOption)
      setHoursPerDay(getDateHours(bid, normalizedClickedDate) || '0')
      setEditingOriginalDate(normalizedClickedDate)
      setHoursError('')
      setChoiceRankError('')
      setEditMode(true)
      setEditDate(normalizedClickedDate)
      setShowHistoryTable(false)
      setBannerMessages(null)
    },
    [committedDates, selectedDates]
  )

  const openNewVacationBidDate = useCallback(
    (clickedDate: string, bid: VacationBidEntry | null) => {
      const normalizedClickedDate = normalizeDate(clickedDate)
      const currentNewDates = selectedDates.filter(
        (date) => !committedDates.includes(normalizeDate(date))
      )

      editingBidRef.current = bid
      if (bid) {
        setEditingBidId(bid.id)
        setHoursPerDate({
          ...buildBidHoursPerDate(bid),
          [normalizedClickedDate]: '0',
        })
      } else {
        setEditingBidId(`NEW-${normalizedClickedDate}-${Date.now()}`)
        setHoursPerDate({ [normalizedClickedDate]: '0' })
      }

      setSelectedDates(Array.from(new Set([...currentNewDates, normalizedClickedDate])))
      // A vacation bid's rank applies to the whole request, not per-date — when
      // "Add date to request" adds a new date to an EXISTING bid, carry its
      // already-chosen rank forward instead of blanking it. Only a genuinely
      // new bid (no existing entry, `bid` null) starts with no rank selected.
      setSelectedRank(
        bid
          ? DROPDOWN_OPTIONS.find((option) => option.value === bid.choiceRank) ?? null
          : null
      )
      setHoursPerDay('0')
      setEditingOriginalDate(normalizedClickedDate)
      setHoursError('')
      setChoiceRankError('')
      setEditMode(true)
      setEditDate(normalizedClickedDate)
      setShowHistoryTable(false)
      setBannerMessages(null)
    },
    [committedDates, selectedDates]
  )

  const handleEditModeVacationBidDateClick = useCallback(
    (normalizedClickedDate: string, activeBid: VacationBidEntry | null): boolean => {
      if (!(editMode && editDate)) return false

      const currentNewDates = selectedDates.filter(
        (date) => !committedDates.includes(normalizeDate(date))
      )

      if (committedDates.includes(normalizedClickedDate)) {
        if (activeBid) {
          openCommittedVacationBidDate(activeBid, normalizedClickedDate)
        }
        return true
      }

      const isAlreadySelectedNewDate = currentNewDates.includes(normalizedClickedDate)
      if (isAlreadySelectedNewDate) {
        const updatedNewDates = currentNewDates.filter(
          (date) => date !== normalizedClickedDate
        )
        setHoursPerDate((prev) => {
          const updated = { ...prev }
          delete updated[normalizedClickedDate]
          return updated
        })
        if (updatedNewDates.length > 0) {
          setSelectedDates(updatedNewDates)
          setEditDate(updatedNewDates[0])
          setHoursPerDay(hoursPerDate[updatedNewDates[0]] || '0')
          setSelectedRank(null)
        } else if (activeBid && editingOriginalDate && committedDates.includes(editingOriginalDate)) {
          // No new dates remain (updatedNewDates is empty here) — return to the
          // bid's own committed date rather than reopening on the date we just
          // deselected. openCommittedVacationBidDate recomputes its "new dates to
          // keep" from this closure's (still-stale) selectedDates, which would
          // re-include the date we just removed, so it can't be reused here.
          setSelectedDates([editingOriginalDate])
          setEditingBidId(activeBid.id)
          setHoursPerDate(buildBidHoursPerDate(activeBid))
          setSelectedRank(
            DROPDOWN_OPTIONS.find((option) => option.value === activeBid.choiceRank) ?? null
          )
          setHoursPerDay(getDateHours(activeBid, editingOriginalDate) || '0')
          setEditDate(editingOriginalDate)
        } else {
          handleBackFromEdit()
        }
        setBannerMessages(null)
        return true
      }

      setSelectedDates((prevDates) =>
        Array.from(new Set([...prevDates, normalizedClickedDate]))
      )
      setHoursPerDate((prev) => ({ ...prev, [normalizedClickedDate]: '0' }))
      setEditDate(normalizedClickedDate)
      setHoursPerDay('0')
      setSelectedRank(null)
      setBannerMessages(null)
      return true
    },
    [committedDates, editDate, editMode, editingOriginalDate, hoursPerDate, openCommittedVacationBidDate, selectedDates]
  )

  const handleDateChange = useCallback((_name: string, value: string | string[]) => {
    setBannerMessages(null)
    // Vacation bids are full-day only. The Datepicker intentionally unblocks partial-day
    // dates so their tooltip renders, delegating rejection to the parent. When a partial-day
    // date appears in the incoming value it means the Datepicker's internal state was already
    // updated to include it (turning the date blue), even though our real `selectedDates`
    // was never changed to include it. The Datepicker only re-syncs its internal highlight
    // from `selectedDates` when that prop's array *reference* changes, so forcing a new
    // reference here (same content) makes it re-sync and drop the erroneous blue highlight
    // (mirrors the filter applied in CreateVacationBidRequest's handleDateChange).
    if (Array.isArray(value)) {
      const hasPartialDay = value.some((dateStr) => {
        const apiKey = convertMMDDYYYYToYYYYMMDD(dateStr)
        return otherRequestDetailsForTooltip[apiKey]?.durationLabel === 'Partial Day'
      })
      if (hasPartialDay) {
        setSelectedDates((prev) => [...prev])
      }
    }
    if (!Array.isArray(value)) return

    const normalizedValues = new Set(value.map(normalizeDate))
    const hasDeselectedCommittedDate = committedDates.some(
      (date) => allBidDates.includes(date) && !normalizedValues.has(date)
    )
    if (hasDeselectedCommittedDate) {
      setVacationBids((prev) => [...prev])
    }
  }, [otherRequestDetailsForTooltip])

  const handleDateClick = useCallback((clickedDate: string) => {
    // Defense-in-depth: reject past dates even if the Datepicker's disabled guard
    // is somehow bypassed (e.g., via the tooltip "See more" path).
    if (isPastDate(clickedDate)) return
    // Vacation bids are full-day only — reject any date that already has a
    // partial-day request from another employee. The Datepicker deliberately
    // unblocks partial-day dates so tooltips can render, so this guard must
    // live here in the parent callback (mirrors CreateVacationBidRequest).
    const apiKey = convertMMDDYYYYToYYYYMMDD(clickedDate)
    if (otherRequestDetailsForTooltip[apiKey]?.durationLabel === 'Partial Day') return
    const normalizedClickedDate = normalizeDate(clickedDate)
    const activeBid = editingBidRef.current ?? vacationBids[0] ?? null
    if (handleEditModeVacationBidDateClick(normalizedClickedDate, activeBid)) return
    
    // Not in edit mode - enter edit mode with this clicked date
    // Check if this date is part of an existing bid
    const existingBid = vacationBids.find((bid) => bid.dates.map(normalizeDate).includes(normalizedClickedDate))
    
    if (existingBid) {
      openCommittedVacationBidDate(existingBid, normalizedClickedDate)
    } else {
      openNewVacationBidDate(normalizedClickedDate, vacationBids[0] ?? null)
    }
  }, [vacationBids, otherRequestDetailsForTooltip, handleEditModeVacationBidDateClick, openCommittedVacationBidDate, openNewVacationBidDate])

  const handleEditBid = (id: string, dateStr: string) => {
    const bidToEdit = vacationBids.find((b) => b.id === id)
    if (!bidToEdit) {
      return
    }

    const originalHours = getDateHours(bidToEdit, dateStr)
    
    // Store the bid so we can restore it if user cancels
    editingBidRef.current = bidToEdit
    
    const normalizedDate = normalizeDate(dateStr)
    setSelectedDates([normalizedDate])
    setEditingOriginalDate(normalizedDate)
    setHoursPerDay(originalHours)
    
    // Initialize hoursPerDate with original dates and their hours
    const initialHoursPerDate: Record<string, string> = {}
    bidToEdit.dates.forEach((date) => {
      initialHoursPerDate[date] = getDateHours(bidToEdit, date)
    })
    setHoursPerDate(initialHoursPerDate)
    
    const selectedOption = DROPDOWN_OPTIONS.find((opt) => opt.value === bidToEdit.choiceRank) || null
    setSelectedRank(selectedOption)

    // Clear error states when entering edit mode
    setHoursError('')
    setChoiceRankError('')
    setBannerMessages(null)

    setEditingBidId(id)
    setEditMode(true)
    setEditDate(normalizedDate)
    setShowHistoryTable(false)
  }

  const handleRemoveBid = (bidId: string, dateStr: string) => {
    const updateBidWithRemovedDate = (bid: VacationBidEntry) => {
      if (bid.id !== bidId) return bid
      const updatedDates = bid.dates.filter((d) => d !== dateStr)
      if (updatedDates.length === 0) return null
      return { ...bid, dates: updatedDates }
    }

    setVacationBids((prev) =>
      prev
        .map(updateBidWithRemovedDate)
        .filter((b): b is VacationBidEntry => b !== null)
    )
    
    // Clear editingOriginalDate if it was removed
    if (editingOriginalDate === dateStr) {
      setEditingOriginalDate(null)
      setHoursPerDay('0')
    }
  }

  const handleBackFromEdit = () => {
    editingBidRef.current = null
    setEditMode(false)
    setEditDate(null)
    setEditingBidId(null)
    setSelectedDates([])
    setHoursPerDay('0')
    setHoursPerDate({})
    setSelectedRank(null)
    setHoursError('')
    setEditingOriginalDate(null)
    setChoiceRankError('')
    setShowHistoryTable(true)
    setBannerMessages(null)
  }

  const validateHours = (hours: string): string | null => {
    if (!hours) return 'Revise'
    const numHours = Number.parseFloat(hours)
    if (Number.isNaN(numHours)) return 'Revise'
    if (numHours < 0.25 || numHours > 16) return 'Revise'
    return null
  }

  const handleUpdateBid = () => {
    const errorMessages: string[] = []
    let hasHoursError = false
    let hasChoiceRankError = false

    // Build final hoursPerDate object with all tracked hours for selected dates
    const finalHoursPerDate: Record<string, string> = {}
    
    selectedDates.forEach((date) => {
      // Use tracked value from hoursPerDate if it exists, otherwise use input value
      finalHoursPerDate[date] = hoursPerDate[date] || hoursPerDay || '8'
    })
    
    // Validate all hours values
    for (const hours of Object.values(finalHoursPerDate)) {
      const hoursValidationError = validateHours(hours)
      if (hoursValidationError) {
        hasHoursError = true
        setHoursError(hoursValidationError)
        errorMessages.push('Request for Vacation must be at least 15 minutes and at most 16 hours duration.')
        break
      } else {
        const { messages: entryMessages } = validateVacationBiddingEntries({ vacation: hours })
        if (entryMessages?.includes(ValidationMessages.DURATION)) {
          hasHoursError = true
          errorMessages.push(ValidationMessages.DURATION)
          break
        }
      }
    }

    // Validate choice rank is selected
    if (!selectedRank) {
      hasChoiceRankError = true
      setChoiceRankError('Choice/Rank is required')
      errorMessages.push('Choice/Rank is required')
    }

    if (hasHoursError || hasChoiceRankError) {
      setBannerMessages(errorMessages)
      scrollToTopAndFocusAlert()
      return
    }

    // Update the vacation bid with per-date hours
    if (editingBidId && editDate) {
      const originalBid = editingBidRef.current
      const originalHoursMap: Record<string, string> = {}
      originalBid?.dates.forEach((date: string) => {
        const normalizedDate = normalizeDate(date)
        originalHoursMap[normalizedDate] =
          hoursPerDate[normalizedDate] ||
          getDateHours(originalBid, normalizedDate)
      })

      const mergedHoursPerDate = { ...originalHoursMap, ...finalHoursPerDate }
      const mergedDates = Array.from(
        new Set([...(originalBid?.dates.map(normalizeDate) ?? []), ...selectedDates.map(normalizeDate)])
      )

      // Check the full resulting date set (existing + newly edited dates) for
      // gaps exceeding 8 days, matching the create-flow validation.
      const dateGapError = validateDateGaps(mergedDates)
      if (dateGapError) {
        setBannerMessages([dateGapError])
        scrollToTopAndFocusAlert()
        return
      }

      const totalHours = Object.values(mergedHoursPerDate).reduce(
        (sum, h) => sum + (Number.parseFloat(h) || 0),
        0
      )
      
      const updatedBid: VacationBidEntry = {
        id: editingBidId,
        dates: mergedDates,
        hoursPerDay: mergedHoursPerDate,
        totalHours,
        comments: '',
        choiceRank: selectedRank?.value,
      }
      
      setVacationBids((prev) => {
        const hasExistingBid = prev.some((b) => b.id === editingBidId)
        if (hasExistingBid) {
          return prev.map((bid) => (bid.id === editingBidId ? updatedBid : bid))
        }
        return [...prev, updatedBid]
      })
      
      // Clear the editing bid ref since we've successfully updated
      editingBidRef.current = null
      
      setHoursError('')
      setChoiceRankError('')
      
      handleBackFromEdit()
    }
  }

  const handleCancelEdit = () => {
    handleBackFromEdit()
  }

  const handleCancel = () => {
    // If parent provided an onCancel handler, prefer that so callers can control navigation
    if (onCancel) {
      onCancel()
    }
  }

    const handleAddMoreDates = () => {
      setIsAddingMoreDates(true)
      setShowHistoryTable(false)
      setEditMode(false)

      // Point the calendar at the most recent date already in the review list.
      // viewedMonth/viewedYear are NOT wired to the Datepicker in calendarView
      // mode — the calendar only re-navigates its displayed month when the
      // `editDate` prop changes (Datepicker.tsx's controlled-`value` effect
      // explicitly skips re-navigating once dates are already selected, "never
      // jump the calendar back when the user adds additional dates"). Setting
      // editDate here (with editMode left false so no edit form is shown) is
      // what actually moves the calendar.
      const allDates = vacationBids.flatMap((bid) => bid.dates)
      if (allDates.length > 0) {
        const lastDate = allDates.reduce((latest, current) =>
          new Date(current).getTime() > new Date(latest).getTime() ? current : latest
        , allDates[0])
        setEditDate(lastDate)
      } else {
        // No dates remain in the table (e.g. the last one was just removed) — the
        // Datepicker's `value` prop is always [] in this history-table view, so
        // without an explicit editDate it would default to today's month instead
        // of the request's original month. viewedMonth/viewedYear still hold that
        // month since they're only set once at mount and never reset on removal.
        setEditDate(`${String(viewedMonth + 1).padStart(2, '0')}/01/${viewedYear}`)
      }
  }

  if (isSubmitting) return <Loader />

  return (
    <div className="page-container min-h-[70vh] lg:mx-40! desktop:mx-[120px]! tablet:mx-5! mobile:mx-4!">
      <BreadCrumbs previousPage="TORT" currentPage="Edit and Resubmit Vacation Bid" onPreviousClick={handleCancel} />
        {isAdminMode && <div className='my-8!'><ManualTimeOffBanner/></div>}
      <h1 className="h1 desktop:mt-[60px]! tablet:mt-[50px]! mobile:mt-8!">Edit and Resubmit Vacation Bid</h1>
      {bannerMessages && bannerMessages.length > 0 && (
        <div className="space-y-3 mb-8! mt-8!">
          {bannerMessages.map((msg) => (
            <ValidationBanner
              key={msg}
              variant="error"
              heading={<p className="p1 -medium">{msg}</p>}
              message={getValidationBannerSubMessage(msg)}
              compact={!isExpandedValidationBanner(msg)}
            />
          ))}
        </div>
      )}

      <div className="mt-8 flex flex-col lg:flex-row gap-4 lg:gap-8 items-start">
          <div className={showHistoryTable ? 'hidden' : 'w-full lg:w-auto'}>
              <div className="flex items-center">
                <h2 className="h2 text-lg mb-3">Select Dates</h2>
                {/* Colour legend tooltip */}
                <div className="ml-2">
                  <ColorLegendTooltip />
                </div>
              </div>
            <Datepicker
              name="vacationDates"
              label="Select Dates"
              value={selectedDates}
              onChange={handleDateChange}
              onDateClick={handleDateClick}
              onMonthYearChange={handleMonthYearChange}
              openBehaviour="calendarView"
              isEditMode={editMode && !!editDate}
              editDate={editDate || ''}
              editModeSelectedDates={selectedDates}
              canEnterEditMode={true}
              highlightVacationBidOnHold
              committedDates={committedDates}
              otherSelectedDatesToShow={otherRequestDates}
              otherRequestDetails={otherRequestDetailsForTooltip}
              calendarWidth={370}
              calendarHeight={399}
              disableCommittedDateClick={isAddingMoreDates}
            />
          </div>

        <div className="w-full lg:flex-1">
          {editMode && editDate && !showHistoryTable && (
            <>
              <div className="mb-6 pb-4 border-b border-gray-300">
                <h2 className="h2 text-lg md:text-xl m-0">Edit Vacation Bid</h2>
                <p className="p3 -book font-bold! pt-2 text-sm md:text-base">Requesting Time Off Hours For:</p>
                {sortedCommittedDates.length > 0 && (
                  <ul className="list-disc ml-5! pt-2">
                    {sortedCommittedDates.map((date) => (
                      <li key={date} className="p3 -book text-sm md:text-base">
                        {date}
                      </li>
                    ))}
                  </ul>
                )}
                {sortedCurrentlyAddingDates.length > 0 && (
                  <>
                    <p className="p3 -book font-bold! pt-3 text-sm md:text-base">Currently adding:</p>
                    <ul className="list-disc ml-5! pt-2">
                      {sortedCurrentlyAddingDates.map((date) => (
                        <li key={date} className="p3 -book text-sm md:text-base">
                          {date}
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </div>

              <p className="p3 -medium pt-4 text-sm md:text-base">Duration</p>
              <div className="flex flex-col gap-3 pt-2">
                <label htmlFor="radio-duration-full" className="inline-flex items-center gap-3 cursor-pointer">
                  <input
                    id="radio-duration-full"
                    name="radio-duration"
                    type="radio"
                    value="Full Day"
                    defaultChecked
                    className="radio-button"
                  />
                  <span className="p3 -medium text-sm md:text-base">Full Day</span>
                </label>
              </div>

              <div className="pt-4 mt-6!">
                <div className="hidden md:flex gap-2 md:gap-6 items-start pb-3 border-b border-gray-300 w-full">
                  <div className="w-full md:w-auto md:min-w-[250px]">
                    <p className="p3 -medium text-sm md:text-base">Hours Per Day</p>
                  </div>
                  <div className="flex gap-2 md:gap-4 w-full md:w-auto md:flex-1">
                    <div className="flex-1">
                      <p className="p3 -medium text-sm md:text-base">Time Off Type</p>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col md:flex-row gap-2 md:gap-6 items-start pt-2 md:pt-4 mb-8 pb-6">
                  <div className="w-full md:w-auto md:min-w-[200px]">
                    <TextInputField
                      id="hours-input-edit"
                      name="hoursPerDay"
                      label="Hours Per Day"
                      type="text"
                      placeholder=""
                      value={hoursPerDay}
                      onChange={(e) => {
                        handleHoursChange(e.target.value)
                        if (hoursError) setHoursError('')
                      }}
                      error={hoursError}
                      customStyles={{ width: '100%' }}
                    />
                  </div>
                  <div className="flex items-center lg:mt-4! justify-center w-full md:w-auto h-full">
                    <p className="p3 -book text-sm md:text-base text-center pl-9!">
                      Vacation Bidding<span className="text-red-500">*</span>
                    </p>
                  </div>
                </div>
              </div>

              <p className="p2 -book text-sm md:text-base mt-4!">
                <span className="text-red-500">*</span>Vacation Bidding Requests do not update your Vacation (PTO/ETO) Available Balance
                until they have been approved.
              </p>

              <div className="mt-3 w-full md:w-auto lg:max-w-xs">
                <label htmlFor="choice-rank-dropdown-edit" className="ds-dropdown__label">
                  <span className="ds-dropdown__label-text">
                    Choice Rank<sup className="align-super text-red-500">*</sup>
                  </span>
                </label>
                <div>
                  <Dropdown
                    selectedValue={selectedRank || undefined}
                    options={DROPDOWN_OPTIONS}
                    onSelect={(option) => {
                      setSelectedRank(option)
                      if (choiceRankError) setChoiceRankError('')
                    }}
                    placeholder="Select a choice rank"
                    menuClassName="max-w-full max-h-[20rem]!"
                  />
                  {choiceRankError && <ValidationErrorDisplay message={choiceRankError} id="choice-rank-error-edit" />}
                </div>
              </div>

              <p className="p2 -book text-sm md:text-base">
                {formatHours(Number.parseFloat(hoursPerDay) || 0)} Hours requested per day
              </p>
              <p className="p2 -book text-sm md:text-base">
                {displayedTotalHours} Hours Total
              </p>

              <div className="flex flex-col-reverse md:flex-row items-stretch md:items-center gap-3 mt-8! pb-6">
                <button type="button" className="button -light block w-full md:w-auto" onClick={handleBackFromEdit}>
                  Back
                </button>
                <button type="button" className="button -light block w-full md:w-auto" onClick={handleCancelEdit}>
                  Cancel Edit
                </button>
                <button type="button" className="button -primary -light block w-full md:w-auto" onClick={handleUpdateBid}>
                  Update
                </button>
              </div>
            </>
          )}

          {!editMode && showHistoryTable && (
            <>
              <h2 className="h2 m-0 mb-4 text-lg md:text-xl">
                {vacationBids.length > 0 ? 'Dates in Request' : 'Edit Your Request for Resubmission'}
              </h2>
              <p className="p3 -book">
                To add a date, select the Add date to request button. To edit or remove a date, you may use the table below.
              </p>
               <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button
                type="button"
                className="button -secondary block w-full! desktop:w-auto!"
                onClick={handleAddMoreDates}
              >
                Add date to request
              </button>
            </div>
              {vacationBids.length > 0 && (
                <>
                  <div className="overflow-x-auto mt-8">
                    <VacationBidTableDisplay
                      vacationBids={vacationBids}
                      onEdit={handleEditBid}
                      onRemove={handleRemoveBid}
                      otherReqData={otherReqData}
                      currentRequestId={data?.requestId}
                    />
                  </div>

                  <div className="form-group margin-top-2u mt-8">
                    <label id="comments-label" className="p4 -medium" htmlFor="comments-textarea">
                      Comments
                    </label>
                    <div className="textarea-wrapper w-full relative">
                      <textarea
                        autoComplete="on"
                        id="comments-textarea"
                        name="comments"
                        aria-labelledby="comments-label comments-count"
                        className={`text-area_word-count text-area_global -book text-base! text-[#0D1C3D]! w-full! ${commentsError ? 'border border-red-500!' : ''}`}
                        data-show-lines="5"
                        rows={5}
                        maxLength={1000}
                        onChange={(e) => {
                          setComments(e.target.value)
                          if (commentsError) setCommentsError('')
                        }}
                        value={comments}
                      />
                      <div className="character-count">
                        <span
                          id="comments-count"
                          aria-live="polite"
                          aria-atomic="true"
                          className="count-area text-input-helper"
                        >
                          {1000 - comments.length} of 1000 characters left
                        </span>
                      </div>
                    </div>
                    {commentsError && <ValidationErrorDisplay message={commentsError} id="comments-error" />}
                  </div>
                  {isAdminMode && (
                    <>
                      <ManualRequestedDate
                        name="admin-datepicker"
                        label="Date Manual Request Received"
                        value={adminReceivedDate}
                        onChange={(v) => {
                          setAdminReceivedDate(v)
                          if (!v.trim()) {
                            setAdminReceivedDateError('')
                            return
                          }
                          const parsed = new Date(v)
                          const today = new Date()
                          today.setHours(23, 59, 59, 999)
                          if (!Number.isNaN(parsed.getTime()) && parsed > today) {
                            setAdminReceivedDateError('Future dates are not allowed for Date Manual Request Received.')
                          } else {
                            setAdminReceivedDateError('')
                          }
                        }}
                        required={true}
                        className="mt-10! w-auto max-w-xs"
                        disableFutureDates={true}
                        error={adminReceivedDateError}
                      />
                      <LabeledValue
                        label="Submitted by Requestor Proxy"
                        value={(() => {
                          const manualEmployee = JSON.parse(
                            sessionStorage.getItem('manualTimeOffEmployee') || '{}'
                          )
                          return `${manualEmployee?.admin?.adminName || 'Employee'} (${manualEmployee?.admin?.adminNuid || 'Unknown'})`
                        })()}
                      />
                    </>
                  )}

                  <div className="flex flex-col-reverse lg:mt-8! md:flex-row items-stretch md:items-center gap-3 mt-8 pb-6">
                    <button type="button" className="button -light block w-full md:w-auto" onClick={handleCancel}>
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="button -primary -light block w-full md:w-auto"
                      onClick={handleResubmit}
                      disabled={isSubmitting || !hasEditsBeenMade}
                    >
                      Resubmit Vacation Bid
                    </button>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default EditAndResubmitVacationBid
